import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import { Save, RefreshCw, Edit2, Check, X } from 'lucide-react'
import { chatPromptsAPI } from '../../lib/api'
import { LoadingSpinner } from '../LoadingSpinner'
import toast from 'react-hot-toast'

interface ChatPrompt {
  id?: string
  position: number
  prompt_text: string
  is_active: boolean
  updated_by?: string
  created_at?: string
  updated_at?: string
}

export function ChatPromptsManager() {
  const [editingPrompt, setEditingPrompt] = useState<number | null>(null)
  const [editValues, setEditValues] = useState<Record<number, string>>({})
  const queryClient = useQueryClient()

  // Fetch all prompts (including inactive ones)
  const { data: prompts, isLoading } = useQuery(
    'chatPrompts',
    chatPromptsAPI.getAllPrompts,
    {
      onSuccess: (data) => {
        // Initialize edit values
        const values: Record<number, string> = {}
        data.forEach((prompt: ChatPrompt) => {
          values[prompt.position] = prompt.prompt_text
        })
        setEditValues(values)
      }
    }
  )

  // Update prompt mutation
  const updatePrompt = useMutation(
    ({ position, prompt_text }: { position: number; prompt_text: string }) =>
      chatPromptsAPI.updatePrompt(position, { prompt_text, is_active: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chatPrompts')
        toast.success('Prompt erfolgreich aktualisiert')
        setEditingPrompt(null)
      },
      onError: (error: any) => {
        toast.error('Fehler beim Aktualisieren: ' + (error.response?.data?.detail || 'Unbekannter Fehler'))
      }
    }
  )

  // Reset prompts mutation
  const resetPrompts = useMutation(
    () => chatPromptsAPI.resetPrompts(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chatPrompts')
        toast.success('Prompts auf Standardwerte zurückgesetzt')
      },
      onError: (error: any) => {
        toast.error('Fehler beim Zurücksetzen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'))
      }
    }
  )

  const handleSave = (position: number) => {
    const newText = editValues[position]?.trim()
    if (!newText) {
      toast.error('Prompt darf nicht leer sein')
      return
    }
    updatePrompt.mutate({ position, prompt_text: newText })
  }

  const handleCancel = (position: number) => {
    const originalPrompt = prompts?.find((p: ChatPrompt) => p.position === position)
    if (originalPrompt) {
      setEditValues(prev => ({
        ...prev,
        [position]: originalPrompt.prompt_text
      }))
    }
    setEditingPrompt(null)
  }

  if (isLoading) {
    return (
      <div className="glass rounded-xl p-8 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  // Ensure we have all 4 prompts
  const promptsArray = Array.from({ length: 4 }, (_, i) => {
    const position = i + 1
    const existingPrompt = prompts?.find((p: ChatPrompt) => p.position === position)
    return existingPrompt || {
      position,
      prompt_text: `Platzhalter ${position}`,
      is_active: true
    }
  })

  return (
    <div className="space-y-6">
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Chat-Vorschläge verwalten</h2>
            <p className="text-gray-400">
              Bearbeiten Sie die Vorschläge, die Benutzern im Chat angezeigt werden.
            </p>
          </div>
          <button
            onClick={() => resetPrompts.mutate()}
            disabled={resetPrompts.isLoading}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Auf Standard zurücksetzen</span>
          </button>
        </div>

        <div className="space-y-4">
          {promptsArray.map((prompt) => (
            <motion.div
              key={prompt.position}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: prompt.position * 0.05 }}
              className="glass rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-sm font-medium text-gray-400">
                      Vorschlag {prompt.position}
                    </span>
                    {prompt.updated_at && (
                      <span className="text-xs text-gray-500">
                        Zuletzt aktualisiert: {new Date(prompt.updated_at).toLocaleDateString('de-DE')}
                      </span>
                    )}
                  </div>
                  
                  {editingPrompt === prompt.position ? (
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        value={editValues[prompt.position] || ''}
                        onChange={(e) => setEditValues(prev => ({
                          ...prev,
                          [prompt.position]: e.target.value
                        }))}
                        className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-primary-500"
                        placeholder="Geben Sie einen Vorschlag ein..."
                        autoFocus
                      />
                      <button
                        onClick={() => handleSave(prompt.position)}
                        disabled={updatePrompt.isLoading}
                        className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCancel(prompt.position)}
                        disabled={updatePrompt.isLoading}
                        className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-gray-300">{prompt.prompt_text}</p>
                      <button
                        onClick={() => setEditingPrompt(prompt.position)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-blue-600/20 border border-blue-600/30 rounded-lg">
          <p className="text-sm text-blue-300">
            <strong>Hinweis:</strong> Diese Vorschläge werden Benutzern angezeigt, wenn sie den Chat öffnen.
            Sie helfen dabei, häufige Fragen schnell zu stellen.
          </p>
        </div>
      </div>
    </div>
  )
}